/*  src/screens/Dashboard/Overview/widgets/hooks/useSupplierAnalytics.ts  */

import {useEffect, useState} from 'react';
import {apiGetSupplierAnalytics, apiGetInputProductNames} from 'constants/Api';
import {ISupplierAnalytics, IFarmInputProduct} from 'constants/interfaces';

/* ───────── helpers ───────── */
const toDate = (d: Date | string): Date =>
  d instanceof Date ? d : new Date(d);

const windowKey = (f: Date, t: Date) =>
  `${f.toISOString().slice(0, 10)}..${t.toISOString().slice(0, 10)}`;

/* hook ─────────────────────────────────────────────────────── */
/* eslint-disable-next-line @typescript-eslint/naming-convention */
export default function useSupplierAnalytics(
  brandIdRaw: number,
  fromRaw: Date | string,
  toRaw: Date | string,
) {
  const brandId = Number(brandIdRaw ?? 0);
  const from = toDate(fromRaw);
  const to = toDate(toRaw);

  const [loading, setLoading] = useState<boolean>(true);
  const [analytics, setAnalytics] = useState<ISupplierAnalytics>();

  useEffect(() => {
    if (!brandId) {
      setLoading(false); // nothing to fetch — don't hang on the skeleton forever
      return;
    }

    let alive = true;
    setLoading(true);

    console.log('[useSupplierAnalytics] fetching', {brandId, from, to});

    (async () => {
      try {
        /* fetch raw analytics */
        const a = await apiGetSupplierAnalytics(from, to, brandId);

        /* hydrate “mostPopular” with product meta */
        const ids = a.mostPopular.map((m) => m.productId);
        if (ids.length) {
          const products = await apiGetInputProductNames(ids);
          a.mostPopular.forEach((row) => {
            row.product = products.find(
              (p: IFarmInputProduct) => p.id == row.productId,
            );
          });
        }

        console.log('[useSupplierAnalytics] response', a);

        alive && setAnalytics(a);
      } catch (err) {
        console.error('[useSupplierAnalytics] fetch error', err);
      } finally {
        alive && setLoading(false);
      }
    })();

    return () => {
      alive = false;
    };
  }, [brandId, windowKey(from, to)]);

  return {loading, analytics};
}
